/**
 * Type-safe обёртки рабочих экранов менеджера: проживания + номера
 * (спринт 11, флот). Поверх готового бэка internal/stay, internal/room.
 *
 * Гейтинг мутаций — на бэке (cmd/api/main.go: /v1/rooms** POST/PATCH →
 * mgmt+housekeeper; #331). Фронт дополнительно скрывает контролы по роли.
 */
import { customFetch } from './openapi-fetch.js';
import type {
  CalendarResponse,
  CreateRoomInput,
  CreateRoomTypeInput,
  CreateStayInput,
  FloorsResponse,
  RoomBoardResponse,
  RoomListResponse,
  RoomStatus,
  RoomStatusHistoryResponse,
  RoomTypeView,
  RoomTypesListResponse,
  RoomView,
  StayListParams,
  StayListResponse,
  StayResponse,
  UpdateRoomInput,
  UpdateRoomStatusInput,
  UpdateRoomTypeInput,
} from '@hsc/types';

/* ===================== Номера ===================== */

export interface ListRoomsParams {
  floor?: number;
  status?: RoomStatus | '';
  limit?: number;
  offset?: number;
}

/** GET /v1/rooms — номера тенанта (tenant-context), с фильтрами. */
export async function listRooms(params: ListRoomsParams = {}): Promise<RoomListResponse> {
  const q: Record<string, string | number> = {};
  if (params.floor !== undefined) q.floor = params.floor;
  if (params.status !== undefined && params.status !== '') q.status = params.status;
  if (params.limit !== undefined) q.limit = params.limit;
  if (params.offset !== undefined) q.offset = params.offset;
  return customFetch<RoomListResponse>({ url: '/rooms', method: 'GET', params: q });
}

/** GET /v1/rooms/floors — агрегат по этажам (всего/свободно). */
export async function listFloors(): Promise<FloorsResponse> {
  return customFetch<FloorsResponse>({ url: '/rooms/floors', method: 'GET' });
}

/**
 * GET /v1/rooms/board — список номеров §4.2 (статус + чистота + последняя
 * уборка + текущий гость/до + «в очереди на уборку»). Один запрос, бэк
 * джойнит rooms + overlay + текущее проживание. Mgmt/staff (гость 403).
 */
export async function listRoomBoard(): Promise<RoomBoardResponse> {
  return customFetch<RoomBoardResponse>({ url: '/rooms/board', method: 'GET' });
}

/**
 * GET /v1/rooms/{id}/status-history — история смены статуса номера с
 * инициатором/источником (checkout / cleaning_request / manager / system).
 */
export async function getRoomStatusHistory(id: string): Promise<RoomStatusHistoryResponse> {
  return customFetch<RoomStatusHistoryResponse>({
    url: `/rooms/${encodeURIComponent(id)}/status-history`,
    method: 'GET',
  });
}

/** GET /v1/rooms/calendar?from=&to= — занятость номеров за окно. */
export async function getRoomsCalendar(from: string, to: string): Promise<CalendarResponse> {
  return customFetch<CalendarResponse>({
    url: '/rooms/calendar',
    method: 'GET',
    params: { from, to },
  });
}

/** PATCH /v1/rooms/{id}/status — смена статуса номера (mgmt/housekeeper). */
export async function updateRoomStatus(
  id: string,
  input: UpdateRoomStatusInput,
): Promise<RoomView> {
  return customFetch<RoomView>({
    url: `/rooms/${encodeURIComponent(id)}/status`,
    method: 'PATCH',
    data: input,
  });
}

/** POST /v1/rooms — создать номер (mgmt). Тело несёт tenant_id (бэк-контракт). */
export async function createRoom(input: CreateRoomInput): Promise<RoomView> {
  return customFetch<RoomView>({ url: '/rooms', method: 'POST', data: input });
}

/** GET /v1/rooms/{id} — карточка номера (включая фото). */
export async function getRoomById(id: string): Promise<RoomView> {
  return customFetch<RoomView>({ url: `/rooms/${encodeURIComponent(id)}`, method: 'GET' });
}

/**
 * PATCH /v1/rooms/{id} — правка номера (номер/этаж/тип/фото), mgmt.
 * Дубль номера отеля бэк вернёт 409 (room.number_taken).
 */
export async function updateRoom(id: string, input: UpdateRoomInput): Promise<RoomView> {
  return customFetch<RoomView>({
    url: `/rooms/${encodeURIComponent(id)}`,
    method: 'PATCH',
    data: input,
  });
}

/**
 * GET /v1/rooms/types?tenant_id= — внутренний реестр типов номеров (полные
 * поля: цена/валюта/вместимости/статус), нужный формам управления фондом.
 * Публичный /v1/public/{slug}/room-types отдаёт усечённую витрину.
 */
export async function listRoomTypesAdmin(tenantID: string): Promise<RoomTypesListResponse> {
  const res = await customFetch<{ room_types: RoomTypeView[] }>({
    url: '/rooms/types',
    method: 'GET',
    params: { tenant_id: tenantID },
  });
  return res.room_types ?? [];
}

/** POST /v1/rooms/types — создать тип номера (mgmt). Тело несёт tenant_id. */
export async function createRoomType(input: CreateRoomTypeInput): Promise<RoomTypeView> {
  return customFetch<RoomTypeView>({ url: '/rooms/types', method: 'POST', data: input });
}

/** PATCH /v1/rooms/types/{id} — правка типа номера (mgmt). */
export async function updateRoomType(
  id: string,
  input: UpdateRoomTypeInput,
): Promise<RoomTypeView> {
  return customFetch<RoomTypeView>({
    url: `/rooms/types/${encodeURIComponent(id)}`,
    method: 'PATCH',
    data: input,
  });
}

/**
 * Подтверждение осмотра номера уборщицей (блок 6 «Облака»). После выезда
 * гостя номер уходит в статус cleaning (housekeeping-очередь); уборщица,
 * закончив уборку и осмотр, переводит его в available («Свободен»). Это
 * частный случай PATCH /v1/rooms/{id}/status, гейтится так же (mgmt +
 * housekeeper, #331). Отдельная обёртка — ради читаемости вызова на экране
 * уборщицы и единого комментария-примечания осмотра.
 */
export async function confirmRoomInspection(
  id: string,
  notes = 'Осмотр подтверждён — номер готов',
): Promise<RoomView> {
  return updateRoomStatus(id, { status: 'available', notes });
}

/**
 * Действие горничной «Номер проверен» (процесс выселения, housekeeping wave):
 * pending_inspection «На проверке» → cleaning «На уборке». Это шаг 2 цикла
 * выезда. Частный случай PATCH /v1/rooms/{id}/status (mgmt + housekeeper).
 */
export async function markRoomInspected(
  id: string,
  notes = 'Номер проверен — приступаем к уборке',
): Promise<RoomView> {
  return updateRoomStatus(id, { status: 'cleaning', notes });
}

/**
 * Действие горничной «Закрыть заявку» (шаг 3 цикла выезда): cleaning «На
 * уборке» → available «Свободен». Уборка завершена, номер готов к заселению.
 */
export async function closeCleaningRequest(
  id: string,
  notes = 'Уборка завершена — номер свободен',
): Promise<RoomView> {
  return updateRoomStatus(id, { status: 'available', notes });
}

/**
 * Действие «В ремонт» (найдена поломка при осмотре): pending_inspection →
 * repair «Ремонт». После ремонта номер возвращается в цикл уборки.
 */
export async function sendRoomToRepair(
  id: string,
  notes = 'Найдена поломка — отправлен в ремонт',
): Promise<RoomView> {
  return updateRoomStatus(id, { status: 'repair', notes });
}

/* ===================== Проживания ===================== */

/**
 * GET /v1/stays — серверный список проживаний для экрана «Проживания»
 * (фильтр по статусу + сортировка по номеру/заезду/выезду + OFFSET-
 * пагинация). Без параметра room бэк отдаёт управляющий список
 * {items,total,page,page_size}; гейтится staffPlus. Дефолты — на бэке
 * (status=in_house, sort=check_in asc, page=1, page_size=20).
 */
export async function listStays(params: StayListParams = {}): Promise<StayListResponse> {
  const q: Record<string, string | number> = {};
  if (params.status) q.status = params.status;
  if (params.sort) q.sort = params.sort;
  if (params.order) q.order = params.order;
  if (params.q !== undefined && params.q !== '') q.q = params.q;
  if (params.page !== undefined) q.page = params.page;
  if (params.page_size !== undefined) q.page_size = params.page_size;
  return customFetch<StayListResponse>({ url: '/stays', method: 'GET', params: q });
}

/** POST /v1/stays — создать бронь (mgmt). Форма «+ Бронь» на шахматке. */
export async function createStay(input: CreateStayInput): Promise<StayResponse> {
  return customFetch<StayResponse>({ url: '/stays', method: 'POST', data: input });
}

/** GET /v1/stays/{id} — карточка проживания. */
export async function getStay(id: string): Promise<StayResponse> {
  return customFetch<StayResponse>({ url: `/stays/${encodeURIComponent(id)}`, method: 'GET' });
}

/** POST /v1/stays/{id}/confirm — подтвердить бронь (pending → confirmed). */
export async function confirmStay(id: string): Promise<StayResponse> {
  return customFetch<StayResponse>({
    url: `/stays/${encodeURIComponent(id)}/confirm`,
    method: 'POST',
  });
}

/** POST /v1/stays/{id}/check-in — заезд. */
export async function checkInStay(id: string): Promise<StayResponse> {
  return customFetch<StayResponse>({
    url: `/stays/${encodeURIComponent(id)}/check-in`,
    method: 'POST',
    data: {},
  });
}

/** POST /v1/stays/{id}/check-out — выезд. */
export async function checkOutStay(id: string): Promise<StayResponse> {
  return customFetch<StayResponse>({
    url: `/stays/${encodeURIComponent(id)}/check-out`,
    method: 'POST',
    data: {},
  });
}

/** POST /v1/stays/{id}/cancel — отмена проживания. */
export async function cancelStay(id: string, reason: string): Promise<unknown> {
  return customFetch<unknown>({
    url: `/stays/${encodeURIComponent(id)}/cancel`,
    method: 'POST',
    data: { reason },
  });
}
